// WavTools 클래스들 (간단한 구현)

// Shared AudioContext for iOS compatibility
let sharedAudioContext: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!sharedAudioContext) {
    sharedAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
      sampleRate: 24000 // Try to request 24kHz, but iOS might ignore this
    });
  }
  return sharedAudioContext;
}

export class WavRecorder {
  private sampleRate: number;
  private stream: MediaStream | null = null;
  private isRecording: boolean = false;
  private recordingCallback: ((data: { mono: Int16Array }) => void) | null = null;
  private processor: ScriptProcessorNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;

  constructor(options: { sampleRate: number }) {
    this.sampleRate = options.sampleRate;
  }

  async begin(): Promise<void> {
    try {
      // iOS often requires resuming the context after user interaction
      const context = getAudioContext();
      if (context.state === 'suspended') {
        await context.resume();
      }

      // On iOS, we shouldn't force a sample rate in getUserMedia as it might fail
      // We will handle resampling manually if needed
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('이 브라우저는 오디오 녹음을 지원하지 않습니다.');
      }

      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      console.log('✅ 마이크 스트림 획득 성공');
    } catch (error: any) {
      console.error('❌ 마이크 접근 실패:', error);
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        throw new Error('마이크 권한이 거부되었습니다. 설정에서 마이크 권한을 허용해주세요.');
      }
      throw new Error('마이크 접근에 실패했습니다: ' + (error.message || '알 수 없는 오류'));
    }
  }

  async record(callback: (data: { mono: Int16Array }) => void): Promise<void> {
    if (!this.stream) throw new Error('스트림이 초기화되지 않았습니다.');
    
    console.log('🎤 녹음 시작...');
    this.recordingCallback = callback;
    
    try {
      const context = getAudioContext();
      if (context.state === 'suspended') {
        await context.resume();
      }

      this.source = context.createMediaStreamSource(this.stream);
      
      // ScriptProcessorNode usage
      // Buffer size 4096 gives good balance between latency and performance
      this.processor = context.createScriptProcessor(4096, 1, 1);
      
      this.processor.onaudioprocess = (event) => {
        if (!this.isRecording || !this.recordingCallback) return;
        
        const inputBuffer = event.inputBuffer;
        const inputData = inputBuffer.getChannelData(0);
        
        // Downsample if necessary (e.g. 44.1kHz/48kHz -> 24kHz)
        let processedData: Float32Array = inputData;
        if (context.sampleRate !== this.sampleRate) {
            processedData = this.downsampleBuffer(inputData, context.sampleRate, this.sampleRate);
        }
        
        // Float32Array to Int16Array conversion
        const int16Data = new Int16Array(processedData.length);
        for (let i = 0; i < processedData.length; i++) {
          const sample = Math.max(-1, Math.min(1, processedData[i]));
          int16Data[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
        }
        
        this.recordingCallback({ mono: int16Data });
      };
      
      this.source.connect(this.processor);
      this.processor.connect(context.destination);
      
      this.isRecording = true;
      console.log(`✅ 실시간 오디오 처리 시작 (Native: ${context.sampleRate}Hz -> Target: ${this.sampleRate}Hz)`);
      
    } catch (error) {
      console.error('❌ 오디오 컨텍스트 설정 실패:', error);
      throw error; 
    }
  }

  // Linear Interpolation Downsampler
  private downsampleBuffer(buffer: Float32Array, sampleRate: number, outSampleRate: number): Float32Array {
    if (outSampleRate === sampleRate) {
      return buffer;
    }
    if (outSampleRate > sampleRate) {
      return buffer; 
    }
    
    const sampleRateRatio = sampleRate / outSampleRate;
    const newLength = Math.round(buffer.length / sampleRateRatio);
    const result = new Float32Array(newLength);
    
    for (let i = 0; i < newLength; i++) {
      const position = i * sampleRateRatio;
      const index = Math.floor(position);
      const fraction = position - index;

      if (index + 1 < buffer.length) {
        // Linear Interpolation: y = y0 + (y1 - y0) * fraction
        result[i] = buffer[index] + (buffer[index + 1] - buffer[index]) * fraction;
      } else {
        result[i] = buffer[index];
      }
    }
    return result;
  }

  async pause(): Promise<void> {
    console.log('⏸️ 녹음 중지...');
    this.isRecording = false;
    
    if (this.processor) {
      this.processor.disconnect();
      this.processor = null;
    }
    
    if (this.source) {
        this.source.disconnect();
        this.source = null;
    }
    
    // Do NOT close the shared context here, just disconnect nodes
    console.log('✅ 녹음 중지 완료');
  }

  async end(): Promise<void> {
    await this.pause();
    
    if (this.stream) {
      this.stream.getTracks().forEach(track => {
        track.stop();
        console.log('🔇 오디오 트랙 정지됨');
      });
      this.stream = null;
    }
    
    this.recordingCallback = null;
    console.log('🧹 WavRecorder 정리 완료');
  }
}

export class WavStreamPlayer {
  private sampleRate: number;
  private currentSources: AudioBufferSourceNode[] = [];
  private audioQueue: { data: string; trackId: string }[] = [];
  private isPlaying: boolean = false;
  private playbackStartTime: number = 0;
  private totalSamplesPlayed: number = 0;
  // 새 요청을 보낸 직후에도 이전 응답의 in-flight chunk가 잠시 더 도착할 수 있다.
  // acceptingAfter 시각 이전의 chunk는 모두 drop. resume(graceMs)로 grace period를 설정.
  private acceptingAfter: number = 0;

  constructor(options: { sampleRate: number }) {
    this.sampleRate = options.sampleRate;
  }

  async connect(): Promise<void> {
    const context = getAudioContext();
    if (context.state === 'suspended') {
      await context.resume();
    }
  }

  // 새 요청을 보낸 직후 호출. graceMs 동안은 stale chunks를 무시 (in-flight
  // 잔여 chunk drop), 그 이후 도착하는 chunk만 재생.
  resume(graceMs: number = 200): void {
    this.acceptingAfter = Date.now() + graceMs;
  }

  add16BitPCM(audioData: string, trackId: string): void {
    if (Date.now() < this.acceptingAfter) return;
    const context = getAudioContext();
    if (!context) return;

    this.audioQueue.push({ data: audioData, trackId });

    if (!this.isPlaying) {
      this.processAudioQueue();
    }
  }

  private async processAudioQueue(): Promise<void> {
    const context = getAudioContext();
    if (this.audioQueue.length === 0 || !context) {
      this.isPlaying = false;
      return;
    }

    this.isPlaying = true;
    
    // Collect chunks (min 100ms)
    const minDuration = 0.1; 
    const minSamples = this.sampleRate * minDuration;
    
    let combinedData = '';
    let processedCount = 0;
    
    while (this.audioQueue.length > 0 && processedCount < 5) {
      const item = this.audioQueue.shift()!;
      combinedData += item.data;
      processedCount++;
      
      try {
        const tempBuffer = this.base64ToArrayBuffer(combinedData);
        if (tempBuffer.byteLength >= minSamples * 2) { 
          break;
        }
      } catch {
        break;
      }
    }

    if (combinedData) {
      await this.playAudioChunk(combinedData);
    }

    if (this.audioQueue.length > 0) {
      setTimeout(() => this.processAudioQueue(), 50);
    } else {
      this.isPlaying = false;
    }
  }

  private async playAudioChunk(audioData: string): Promise<void> {
    const context = getAudioContext();
    if (!context) return;

    try {
      const buffer = this.base64ToArrayBuffer(audioData);
      const floatArray = new Float32Array(buffer.byteLength / 2);
      const view = new DataView(buffer);
      
      for (let i = 0; i < floatArray.length; i++) {
        floatArray[i] = view.getInt16(i * 2, true) / 32768.0;
      }
      
      if (floatArray.length === 0) return;
      
      // Create buffer with the target sample rate (24000)
      const audioBuffer = context.createBuffer(1, floatArray.length, this.sampleRate);
      audioBuffer.copyToChannel(floatArray, 0);
      
      const source = context.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(context.destination);
      
      const currentTime = context.currentTime;
      let startTime = currentTime;
      
      if (this.playbackStartTime === 0) {
        this.playbackStartTime = currentTime;
      } else {
        const expectedTime = this.playbackStartTime + (this.totalSamplesPlayed / this.sampleRate);
        startTime = Math.max(currentTime, expectedTime);
      }
      
      this.currentSources.push(source);
      source.onended = () => {
        const index = this.currentSources.indexOf(source);
        if (index > -1) this.currentSources.splice(index, 1);
      };
      
      source.start(startTime);
      this.totalSamplesPlayed += floatArray.length;
      
      } catch (error) {
      console.error('오디오 재생 오류:', error);
    }
  }

  async interrupt(): Promise<{ trackId?: string; offset?: number } | null> {
    this.currentSources.forEach(source => {
      try { source.stop(); } catch {}
    });
    this.currentSources = [];
    this.audioQueue = [];
    this.isPlaying = false;
    this.playbackStartTime = 0;
    this.totalSamplesPlayed = 0;
    // 새 chunks를 거부. 새 요청 보낸 직후 resume()으로 grace period 설정 필요.
    this.acceptingAfter = Number.POSITIVE_INFINITY;
    return null;
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }
}

// RealtimeAPI 클래스 - OpenAI Realtime WebSocket 어댑터
// 앱 내부의 send/event 인터페이스는 유지하면서 GPT Realtime 2에 직접 연결한다.
// 입력/출력: 24kHz 16-bit PCM mono
const OPENAI_REALTIME_MODEL = 'gpt-realtime-2';
const DEFAULT_INPUT_TRANSCRIPTION_MODEL = 'gpt-realtime-whisper';

export class RealtimeAPI {
  private apiKey: string;
  private sessionEndpoint: string;
  private ws: WebSocket | null = null;
  private eventHandlers: Map<string, ((data: any) => void)[]> = new Map();
  private messageQueue: any[] = [];
  private isSessionReady: boolean = false;

  constructor(options: { apiKey?: string; sessionEndpoint?: string; dangerouslyAllowAPIKeyInBrowser: boolean; debug: boolean }) {
    this.apiKey = options.apiKey || '';
    this.sessionEndpoint = options.sessionEndpoint || '/api/realtime-session';
  }

  async connect(): Promise<void> {
    const clientSecret = await this.getClientSecret();

    return new Promise((resolve, reject) => {
      const url = `wss://api.openai.com/v1/realtime?model=${encodeURIComponent(OPENAI_REALTIME_MODEL)}`;
      const protocols = [
        'realtime',
        `openai-insecure-api-key.${clientSecret}`
      ];

      this.ws = new WebSocket(url, protocols);

      this.ws.onopen = () => {
        console.log('OpenAI Realtime WebSocket 연결됨');
        resolve();
      };

      this.ws.onmessage = async (event) => {
        try {
          const text = typeof event.data === 'string'
            ? event.data
            : await (event.data as Blob).text();
          const data = JSON.parse(text);

          this.handleOpenAIMessage(data);
        } catch (error) {
          console.error('메시지 파싱 오류:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket 오류:', error);
        reject(new Error('WebSocket 연결에 실패했습니다. 네트워크 연결을 확인해주세요.'));
      };

      this.ws.onclose = (event) => {
        console.log('WebSocket 연결 종료:', event.code, event.reason);
        this.emit('close', {});

        if (event.code === 1008 || event.code === 4001) {
          reject(new Error('인증 실패 - OpenAI API 키를 확인해주세요'));
        } else if (event.code >= 4000 && event.code !== 1000) {
          reject(new Error(`OpenAI Realtime API 오류 (${event.code}): ${event.reason || '알 수 없는 오류'}`));
        }
      };
    });
  }

  private async getClientSecret(): Promise<string> {
    if (this.apiKey) {
      return this.apiKey;
    }

    const response = await fetch(this.sessionEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const text = await response.text();
    const data = text ? this.safeParseJson(text) : null;

    if (!response.ok) {
      const message = data?.error || data?.details?.error?.message || text || 'OpenAI Realtime 세션 생성에 실패했습니다.';
      throw new Error(message);
    }

    const token = data?.value || data?.client_secret?.value;
    if (!token) {
      throw new Error('OpenAI Realtime client secret 응답이 올바르지 않습니다.');
    }

    return token;
  }

  private handleOpenAIMessage(data: any): void {
    const type = data?.type;
    if (!type) return;

    if (type === 'session.created') {
      this.isSessionReady = true;
      this.emit('server.session.created', data);
      this.flushQueue();
      return;
    }

    if (type === 'session.updated') {
      this.emit('server.session.updated', data);
      return;
    }

    if (type === 'error') {
      console.error('OpenAI Realtime API 오류:', data.error);
      this.emit('server.error', data);
      return;
    }

    this.emit(`server.${type}`, data);

    // gpt-realtime-2 문서와 API 레퍼런스가 audio/output_audio 이벤트 이름을 함께 언급한다.
    // 앱 내부 핸들러는 기존 response.audio.* 이름으로 유지한다.
    if (type === 'response.output_audio.delta') {
      this.emit('server.response.audio.delta', data);
    }
    if (type === 'response.output_audio_transcript.delta') {
      this.emit('server.response.audio_transcript.delta', data);
    }
    if (type === 'response.output_audio_transcript.done') {
      this.emit('server.response.audio_transcript.done', data);
    }
    if (type === 'response.output_text.delta' || type === 'response.text.delta') {
      this.emit('server.response.audio_transcript.delta', data);
    }
  }

  async send(eventType: string, data?: any): Promise<void> {
    const message = this.translateToOpenAI(eventType, data);
    if (!message) return;

    if (this.ws?.readyState === WebSocket.OPEN && (this.isSessionReady || eventType === 'session.update')) {
      this.rawSend(message);
    } else {
      this.messageQueue.push(message);
    }
  }

  private rawSend(message: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  private flushQueue(): void {
    while (this.messageQueue.length > 0 && this.ws?.readyState === WebSocket.OPEN) {
      const msg = this.messageQueue.shift();
      this.rawSend(msg);
    }
  }

  private translateToOpenAI(eventType: string, data?: any): any | null {
    switch (eventType) {
      case 'session.update': {
        const session = data?.session || {};
        const outputModalities = session.output_modalities
          || (session.modalities?.includes('audio') ? ['audio'] : ['text']);

        return {
          type: 'session.update',
          session: {
            type: 'realtime',
            model: OPENAI_REALTIME_MODEL,
            instructions: session.instructions || '',
            output_modalities: outputModalities,
            audio: {
              input: {
                format: {
                  type: 'audio/pcm',
                  rate: 24000
                },
                transcription: {
                  model: DEFAULT_INPUT_TRANSCRIPTION_MODEL
                },
                turn_detection: session.turn_detection ?? null
              },
              output: {
                format: {
                  type: 'audio/pcm',
                  rate: 24000
                },
                voice: session.voice || 'alloy'
              }
            }
          }
        };
      }

      case 'input_audio_buffer.clear': {
        return { type: 'input_audio_buffer.clear' };
      }

      case 'input_audio_buffer.append': {
        return { type: 'input_audio_buffer.append', audio: data?.audio || '' };
      }

      case 'input_audio_buffer.commit': {
        return { type: 'input_audio_buffer.commit' };
      }

      case 'response.create': {
        return {
          type: 'response.create',
          response: this.normalizeResponse(data?.response)
        };
      }

      case 'response.cancel': {
        return { type: 'response.cancel' };
      }

      case 'conversation.item.create': {
        const item = data?.item;
        if (!item) return null;
        return {
          type: 'conversation.item.create',
          item
        };
      }

      default:
        console.warn('알 수 없는 이벤트 타입:', eventType);
        return null;
    }
  }

  private normalizeResponse(response?: any): any {
    if (!response) {
      return { output_modalities: ['audio'] };
    }

    const normalized: any = { ...response };

    if (response.modalities && !response.output_modalities) {
      normalized.output_modalities = response.modalities.includes('audio') ? ['audio'] : ['text'];
      delete normalized.modalities;
    }

    if (normalized.output_modalities?.includes('audio')) {
      normalized.audio = {
        ...(normalized.audio || {}),
        output: {
          format: { type: 'audio/pcm' },
          ...(normalized.audio?.output || {})
        }
      };
    }

    return normalized;
  }

  appendInputAudio(audioData: Int16Array): void {
    // Click-to-Record 방식에서는 사용하지 않음
    console.log('appendInputAudio 호출됨 (Click-to-Record 모드에서는 사용 안 함)');
  }

  on(eventType: string, handler: (data: any) => void): void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, []);
    }
    this.eventHandlers.get(eventType)?.push(handler);
  }

  private emit(eventType: string, data: any): void {
    const handlers = this.eventHandlers.get(eventType) || [];
    handlers.forEach((handler: (data: any) => void) => {
      try {
        handler(data);
      } catch (error) {
        console.error(`이벤트 핸들러 오류 (${eventType}):`, error);
      }
    });
  }

  async disconnect(): Promise<void> {
    this.isSessionReady = false;
    this.messageQueue = [];
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  reset(): void {
    this.eventHandlers.clear();
    this.messageQueue = [];
    this.isSessionReady = false;
  }

  private safeParseJson(text: string): any | null {
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }
}
